import { FinancialApplicationSection } from "@/types/enums";
import { atomFamily } from "jotai/utils";
import { atom, Atom, WritableAtom } from "jotai";
import { atomWithHash } from "jotai-location";

export interface BankerApplicationViewState {
  activeTabKey: Atom<string>;
  currentSection: Atom<string>;
  setFinancialApplicationSection: WritableAtom<null, [FinancialApplicationSection], void>;
  usesSticky: Atom<boolean>;
}

export const FINANCIAL_APPLICATION_SECTION_TO_TAB_KEY_MAP: Record<
  FinancialApplicationSection,
  string
> = {
  [FinancialApplicationSection.ProductSelection]: "product-details",
  [FinancialApplicationSection.PersonInformationBasicInfo]: "client-details",
  [FinancialApplicationSection.AccountDetails]: "account-details",
  // [FinancialApplicationSection.PersonInformationContactInfo]: "client-contact",
  // [FinancialApplicationSection.EmploymentInformation]: "client-employment",
  [FinancialApplicationSection.JointAccountOwners]: "joint-account-owners",
  [FinancialApplicationSection.PersonalAccountBeneficiaries]: "personal-account-beneficiaries",
  [FinancialApplicationSection.PersonalAccountAcceptableUse]: "personal-acceptable-use",
  [FinancialApplicationSection.PersonalAccountRiskReview]: "personal-account-risk-review",
  [FinancialApplicationSection.OrganizationInformation]: "organization-info",
  // [FinancialApplicationSection.OrganizationContactInformation]: "organization-contact-info",
  [FinancialApplicationSection.KeyPeople]: "key-people",
  [FinancialApplicationSection.OrganizationAccountAcceptableUse]: "organization-acceptable-use",
  [FinancialApplicationSection.LawFirmOrganizationAccountRiskReview]:
    "law-firm-organization-account-risk-review",
  [FinancialApplicationSection.NonLawFirmOrganizationAccountRiskReview]:
    "non-law-firm-organization-account-risk-review",
  [FinancialApplicationSection.OptIns]: "opt-ins",
  // [FinancialApplicationSection.DigitalBankingInformation]: "digital-banking-info",
  // [FinancialApplicationSection.DebitCardInformation]: "debit-card-info",
  [FinancialApplicationSection.AllSections]: "all-sections",
  // [FinancialApplicationSection.GovernmentIdInformation]: "government-id-info",
  [FinancialApplicationSection.FinancialApplicationDocuments]: "financial-application-documents",
  [FinancialApplicationSection.DisclosureAcknowledgements]: "disclosure-acknowledgements",
};

export const TAB_KEY_TO_FINANCIAL_APPLICATION_SECTION_MAP = Object.fromEntries(
  Object.entries(FINANCIAL_APPLICATION_SECTION_TO_TAB_KEY_MAP).map(([key, value]) => [value, key])
);

export const bankerApplicationViewStateAtomFamily = atomFamily(
  (financialApplicationId: string) => {
    const sectionKey = `financial-application-section-${financialApplicationId}`;
    const currentSection = atomWithHash<string>(sectionKey, "product-details");
    const activeTabKey = atom(
      FINANCIAL_APPLICATION_SECTION_TO_TAB_KEY_MAP[FinancialApplicationSection.ProductSelection]
    );
    const setFinancialApplicationSection = atom(
      null,
      (_get, set, financialApplicationSection: FinancialApplicationSection) => {
        set(
          activeTabKey,
          FINANCIAL_APPLICATION_SECTION_TO_TAB_KEY_MAP[financialApplicationSection]
        );
        set(
          currentSection,
          FINANCIAL_APPLICATION_SECTION_TO_TAB_KEY_MAP[financialApplicationSection]
        );
      }
    );
    const stickyKey = `sticky-banker-application-details-form-${financialApplicationId}`;
    const usesSticky = atomWithHash(stickyKey, true);
    return atom({
      activeTabKey,
      currentSection,
      setFinancialApplicationSection,
      usesSticky,
    });
  },
  (a, b) => a.toLowerCase() === b.toLowerCase()
);
